<template>
    <q-table
        :rows="report.rows"
        :columns="table.columns"
        :pagination="table.pagination"
        :no-data-label="$t('No records found for this date range.')"
        :hide-bottom="report.rows.length > 0"
        :visible-columns="visibleColumns"
        :table-class="
            report.rows.length > 0
                ? 'report-container sticky-table sticky-column'
                : ''
        "
        row-key="assetId"
        class="sticky-column full-width bg-secondary-white no-shadow border-grey"
        :title="label"
        title-class="text-weight-medium text-medium-grey text-body2"
    >
        <!-- Header slot -->
        <template v-slot:header="props">
            <q-tr :props="props">
                <q-th v-for="col in props.cols" :key="col.name" :props="props">
                    <span class="text-weight-bold" style="font-size: 14px">
                        {{ col.label }}
                    </span>
                    <q-tooltip class="text-body2" v-if="col.tipText">
                        {{ col.tipText }}.
                    </q-tooltip>
                </q-th>
            </q-tr>
        </template>

        <!-- Row slot -->
        <template v-slot:body="props">
            <q-tr v-if="props.row.products.length > 0" :props="props">
                <q-td
                    v-for="col in props.cols"
                    :key="col.name"
                    :class="col.class"
                    align="right"
                >
                    <div v-if="col.name === 'asset_name'" class="row">
                        <!-- Expansion button -->
                        <div>
                            <q-btn
                                @click="props.expand = !props.expand"
                                :icon="
                                    props.expand
                                        ? 'fas fa-caret-up'
                                        : 'fas fa-caret-down'
                                "
                                class="q-mr-md"
                                color="accent"
                                size="sm"
                                round
                                dense
                            >
                                <q-tooltip>
                                    {{
                                        props.expand
                                            ? $t('Collapse row')
                                            : $t('Expand row')
                                    }}
                                </q-tooltip>
                            </q-btn>
                            {{ col.value }}
                            <q-badge
                                v-if="isAssetDeleted(props.row.assetId)"
                                color="lighter-grey"
                                text-color="medium-grey"
                            >
                                {{ $t('Deleted') }}
                            </q-badge>
                        </div>
                    </div>
                    <div v-else-if="col.name.includes('quantity')">
                        {{ formatQuantities(col.value) }}
                    </div>
                    <div
                        v-else-if="
                            col.name === 'availability' ||
                            col.name === 'performance' ||
                            col.name === 'quality' ||
                            col.name === 'oee' ||
                            col.name === 'teep'
                        "
                    >
                        {{ formatPercentage(col.value) }}
                    </div>
                    <div
                        v-else-if="
                            col.name.includes('duration') ||
                            col.name === 'unplanned_downtime' ||
                            col.name === 'planned_downtime'
                        "
                    >
                        {{ formatProductionValue(col.value / 60) }}
                    </div>
                    <div
                        v-else-if="
                            col.name === 'total_production_count' ||
                            col.name === 'rejected_production_count' ||
                            col.name === 'net_production_count'
                        "
                    >
                        {{ formatProductionValue(col.value, userLocale) }}
                    </div>
                    <div v-else>
                        {{ formatProductionValue(col.value) }}
                    </div>
                </q-td>
            </q-tr>
            <!-- Expanded row -->
            <q-tr
                v-show="props.expand"
                align="right"
                v-for="(row, index) in props.row.products"
                :key="`${props.row.assetId}-${index}`"
            >
                <q-td class="expanded-child">
                    <div>
                        {{ row['name'] }}
                    </div>
                    <div class="text-caption text-grey-7">
                        {{
                            $t('SKU {sku}', {
                                sku: row['sku']
                            })
                        }}
                    </div>
                </q-td>

                <!-- Quality columns -->
                <template v-if="showColumns.quality">
                    <q-td>
                        {{
                            formatProductionValue(
                                row['quality']['totalProduction'],
                                userLocale
                            )
                        }}
                        <q-icon
                            v-if="row['quality']['totalProduction'] < 0"
                            name="fas fa-exclamation-circle"
                            color="negative"
                        >
                            <q-tooltip>
                                {{ this.negativeValueMessage }}
                            </q-tooltip>
                        </q-icon>
                    </q-td>
                    <q-td>
                        {{ formatQuantities(row['quality']['totalQuantity']) }}
                        <q-icon
                            v-if="
                                hasNegativeValue(
                                    row['quality']['totalQuantity']
                                )
                            "
                            name="fas fa-exclamation-circle"
                            color="negative"
                        >
                            <q-tooltip>
                                {{ this.negativeValueMessage }}
                            </q-tooltip>
                        </q-icon>
                    </q-td>
                    <q-td>
                        {{
                            formatProductionValue(
                                row['quality']['rejectProduction'],
                                userLocale
                            )
                        }}
                        <q-icon
                            v-if="row['quality']['rejectProduction'] < 0"
                            name="fas fa-exclamation-circle"
                            color="negative"
                        >
                            <q-tooltip>
                                {{ this.negativeValueMessage }}
                            </q-tooltip>
                        </q-icon>
                    </q-td>
                    <q-td>
                        {{ formatQuantities(row['quality']['rejectQuantity']) }}
                        <q-icon
                            v-if="
                                hasNegativeValue(
                                    row['quality']['rejectQuantity']
                                )
                            "
                            name="fas fa-exclamation-circle"
                            color="negative"
                        >
                            <q-tooltip>
                                {{ this.negativeValueMessage }}
                            </q-tooltip>
                        </q-icon>
                    </q-td>
                    <q-td>
                        {{
                            formatProductionValue(
                                row['quality']['netProduction'],
                                userLocale
                            )
                        }}
                        <q-icon
                            v-if="row['quality']['netProduction'] < 0"
                            name="fas fa-exclamation-circle"
                            color="negative"
                        >
                            <q-tooltip>
                                {{ this.negativeValueMessage }}
                            </q-tooltip>
                        </q-icon>
                    </q-td>
                    <q-td>
                        {{ formatQuantities(row['quality']['netQuantity']) }}
                        <q-icon
                            v-if="
                                hasNegativeValue(row['quality']['netQuantity'])
                            "
                            name="fas fa-exclamation-circle"
                            color="negative"
                        >
                            <q-tooltip>
                                {{ this.negativeValueMessage }}
                            </q-tooltip>
                        </q-icon>
                    </q-td>
                </template>
                <q-td class="quality-primary">
                    {{ formatPercentage(row['quality']['quality']) }}
                </q-td>

                <!-- Performance columns -->
                <template v-if="showColumns.performance">
                    <q-td>
                        {{
                            formatProductionValue(
                                row['performance']['actualRate']
                            )
                        }}
                    </q-td>
                    <q-td>
                        {{
                            formatProductionValue(
                                row['performance']['plannedRate']
                            )
                        }}
                    </q-td>
                </template>
                <q-td class="performance-primary">
                    {{ formatPercentage(row['performance']['performance']) }}
                </q-td>

                <!-- Availability columns -->
                <template v-if="showColumns.availability">
                    <q-td>
                        {{
                            formatProductionValue(
                                row['availability']['running'] / 60
                            )
                        }}
                    </q-td>
                    <q-td>
                        {{
                            formatProductionValue(
                                row['availability']['changeoverDuration'] / 60
                            )
                        }}
                    </q-td>
                    <q-td>
                        {{
                            formatProductionValue(
                                row['availability']['totalDowntime'] / 60
                            )
                        }}
                    </q-td>
                    <q-td>
                        {{
                            formatProductionValue(
                                row['availability']['availabilityDowntime'] / 60
                            )
                        }}
                    </q-td>
                    <q-td>
                        {{
                            formatProductionValue(
                                row['availability']['performanceDowntime'] / 60
                            )
                        }}
                    </q-td>
                    <q-td>
                        {{
                            formatProductionValue(
                                row['availability']['unplannedDowntime'] / 60
                            )
                        }}
                    </q-td>
                    <q-td>
                        {{
                            formatProductionValue(
                                row['availability']['plannedDowntime'] / 60
                            )
                        }}
                    </q-td>
                </template>
                <q-td class="availability-primary">
                    {{ formatPercentage(row['availability']['availability']) }}
                </q-td>

                <q-td>{{ formatPercentage(row['oee']) }}</q-td>
            </q-tr>
        </template>

        <template v-slot:bottom-row>
            <q-tr
                align="right"
                v-show="report.rows.length > 0"
                class="text-weight-medium bg-secondary-white"
            >
                <q-td align="left" class="bg-secondary-white">{{
                    $t('Grand Total')
                }}</q-td>
                <template v-if="showColumns.quality">
                    <q-td>
                        {{
                            formatProductionValue(
                                report.totals.quality.totalProduction,
                                userLocale
                            )
                        }}
                    </q-td>
                    <q-td>
                        {{
                            formatQuantities(
                                report.totals.quality.totalQuantity
                            )
                        }}
                    </q-td>
                    <q-td>
                        {{
                            formatProductionValue(
                                report.totals.quality.rejectProduction,
                                userLocale
                            )
                        }}
                    </q-td>
                    <q-td>
                        {{
                            formatQuantities(
                                report.totals.quality.rejectQuantity
                            )
                        }}
                    </q-td>
                    <q-td>
                        {{
                            formatProductionValue(
                                report.totals.quality.netProduction,
                                userLocale
                            )
                        }}
                    </q-td>
                    <q-td>
                        {{
                            formatQuantities(report.totals.quality.netQuantity)
                        }}
                    </q-td>
                </template>
                <q-td>
                    {{ formatPercentage(report.totals.quality.quality) }}
                </q-td>
                <template v-if="showColumns.performance">
                    <q-td>
                        {{
                            formatProductionValue(
                                report.totals.performance.actualRate
                            )
                        }}
                    </q-td>
                    <q-td>
                        {{
                            formatProductionValue(
                                report.totals.performance.plannedRate
                            )
                        }}
                    </q-td>
                </template>
                <q-td>
                    {{
                        formatPercentage(report.totals.performance.performance)
                    }}
                </q-td>
                <template v-if="showColumns.availability">
                    <q-td>
                        {{
                            formatProductionValue(
                                report.totals.availability.running / 60
                            )
                        }}
                    </q-td>
                    <q-td>
                        {{
                            formatProductionValue(
                                report.totals.availability.changeoverDuration /
                                    60
                            )
                        }}
                    </q-td>
                    <q-td>
                        {{
                            formatProductionValue(
                                report.totals.availability.totalDowntime / 60
                            )
                        }}
                    </q-td>
                    <q-td>
                        {{
                            formatProductionValue(
                                report.totals.availability
                                    .availabilityDowntime / 60
                            )
                        }}
                    </q-td>
                    <q-td>
                        {{
                            formatProductionValue(
                                report.totals.availability.performanceDowntime /
                                    60
                            )
                        }}
                    </q-td>
                    <q-td>
                        {{
                            formatProductionValue(
                                report.totals.availability.unplannedDowntime /
                                    60
                            )
                        }}
                    </q-td>
                    <q-td>
                        {{
                            formatProductionValue(
                                report.totals.availability.plannedDowntime / 60
                            )
                        }}
                    </q-td>
                </template>
                <q-td>
                    {{
                        formatPercentage(
                            report.totals.availability.availability
                        )
                    }}
                </q-td>
                <q-td>
                    {{ formatPercentage(report.totals.oee) }}
                </q-td>
            </q-tr>
        </template>
    </q-table>
</template>

<script>
import { convertToUnitSystem, getUnitSystemOptions } from '@models/products.js';
import { getLocaleFormat } from '@models/timeformats.js';
import {
    formatPercentage,
    formatProductionValue
} from '@utils/numberFormatters.js';
import { mapGetters } from 'vuex';
import { mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'PerformanceReportTable',
    props: {
        /**
         * Report data.
         */
        report: {
            type: Object,
            required: true
        },
        visibleColumns: {
            type: Array,
            required: true
        },
        showColumns: {
            type: Object,
            required: true
        },
        label: {
            type: String,
            required: false
        },
        facilityId: {
            type: Number,
            required: true
        }
    },
    watch: {
        facilityUnits: {
            handler() {
                // update the column headers for total quantity, reject quantity, and net quantity
                // to include the units of the facility's unit system.
                const units = this.facilityUnits.join(' | ');
                this.table.columns.forEach(column => {
                    if (column.name === 'production_quantity') {
                        column.label = this.$t('Total quantity ({units})', {
                            units: units
                        });
                    } else if (column.name === 'rejection_quantity') {
                        column.label = this.$t('Reject quantity ({units})', {
                            units: units
                        });
                    } else if (column.name === 'net_quantity') {
                        column.label = this.$t('Net quantity ({units})', {
                            units: units
                        });
                    }
                });
            },
            immediate: true
        }
    },
    data() {
        return {
            // table rows
            table: {
                columns: [
                    {
                        name: 'asset_name',
                        field: 'name',
                        label: this.$t('Asset'),
                        align: 'left'
                    },

                    // quality (production) columns
                    {
                        name: 'total_production_count',
                        field: row => row.quality.totalProduction,
                        label: this.$t('Total production (count)'),
                        align: 'right',
                        tipText: this.$t(
                            'The total number of units or items produced during a specific period, including both good and rejected items'
                        )
                    },
                    {
                        name: 'production_quantity',
                        field: row => row.quality.totalQuantity,
                        label: this.$t('Total quantity'),
                        align: 'right',
                        tipText: this.$t(
                            'The total quantity or volume produced during a specific period (measured in litre, kg, lb, or gal), including both good and rejected quantities'
                        )
                    },
                    {
                        name: 'rejected_production_count',
                        field: row => row.quality.rejectProduction,
                        label: this.$t('Rejected production (count)'),
                        align: 'right',
                        tipText: this.$t(
                            'The number of units or items produced during a specific period that are considered defective, substandard, or non-conforming and are rejected or classified as scrap'
                        )
                    },
                    {
                        name: 'rejection_quantity',
                        field: row => row.quality.rejectQuantity,
                        label: this.$t('Reject quantity'),
                        align: 'right',
                        tipText: this.$t(
                            'The quantity or volume of a product produced during a specific period that is considered defective, substandard, or non-conforming and is rejected or classified as scrap (measured in litre, kg, lb, or gal)'
                        )
                    },
                    {
                        name: 'net_production_count',
                        field: row => row.quality.netProduction,
                        label: this.$t('Net production (count)'),
                        align: 'right',
                        tipText: this.$t(
                            'The total number of units or items produced during a specific period after subtracting the rejected production count'
                        )
                    },
                    {
                        name: 'net_quantity',
                        field: row => row.quality.netQuantity,
                        label: this.$t('Net quantity'),
                        align: 'right',
                        tipText: this.$t(
                            'The total quantity or volume of a product produced during a specific period after subtracting the rejected quantity (measured in litre, kg, lb, or gal)'
                        )
                    },
                    {
                        name: 'quality',
                        field: row => row.quality.quality,
                        label: this.$t('Quality'),
                        align: 'right',
                        class: 'quality-primary',
                        tipText: this.$t(
                            'Assesses the proportion of good-quality products or units produced compared to the total output, considering defects, rework, or scrap'
                        )
                    },

                    // performance columns
                    {
                        name: 'actual_run_rate',
                        field: row => row.performance.actualRate,
                        label: this.$t('Actual rate (#/min)'),
                        align: 'right',
                        tipText: this.$t(
                            'The rate at which the equipment or machinery is producing output in terms of the number of units or items per minute. It represents the actual production speed observed during a specific period'
                        )
                    },
                    {
                        name: 'planned_rate',
                        field: row => row.performance.plannedRate,
                        label: this.$t('Max run rate (#/min)'),
                        align: 'right',
                        tipText: this.$t(
                            'The target or expected rate at which the equipment or machinery is designed or scheduled to produce output in terms of the number of units or items per minute. It represents the ideal or planned production speed for a specific period'
                        )
                    },
                    {
                        name: 'performance',
                        field: row => row.performance.performance,
                        label: this.$t('Performance'),
                        align: 'right',
                        class: 'performance-primary',
                        tipText: this.$t(
                            'Evaluates the actual production speed compared to the ideal or designed speed, taking into account any slowdowns or stoppages'
                        )
                    },

                    // availability columns
                    {
                        name: 'running_duration',
                        field: row => row.availability.running,
                        label: this.$t('Running (min)'),
                        align: 'right',
                        tipText: this.$t(
                            'The total duration of time that the equipment or machinery is in operation and actively producing output'
                        )
                    },
                    {
                        name: 'changeover_duration',
                        field: row => row.availability.changeoverDuration,
                        label: this.$t('Switch (min)'),
                        align: 'right',
                        tipText: this.$t(
                            'The amount of time taken to switch or change over between different products, setups, or processes, which can result in temporary non-production'
                        )
                    },
                    {
                        name: 'downtime_duration',
                        field: row => row.availability.totalDowntime,
                        label: this.$t('Total downtime (min)'),
                        align: 'right',
                        tipText: this.$t(
                            'The sum of all the periods when the equipment or machinery is not running, including both planned and unplanned downtime'
                        )
                    },
                    {
                        name: 'availability_downtime_duration',
                        field: row => row.availability.availabilityDowntime,
                        label: this.$t('Availability losses (min)'),
                        align: 'right',
                        tipText: this.$t(
                            'The cumulative time during which the equipment is intended to run but is not due to factors such as breakdowns, malfunctions, maintenance, or other planned events'
                        )
                    },
                    {
                        name: 'performance_downtime_duration',
                        field: row => row.availability.performanceDowntime,
                        label: this.$t('Performance losses (min)'),
                        align: 'right',
                        tipText: this.$t(
                            'The accumulated time during which the equipment is running but not achieving its maximum designed or expected speed or efficiency due to factors such as slow cycles, minor stoppages, or reduced performance'
                        )
                    },
                    {
                        name: 'unplanned_downtime',
                        field: row => row.availability.unplannedDowntime,
                        label: this.$t('Unplanned downtime (min)'),
                        align: 'right',
                        tipText: this.$t(
                            'Unscheduled or unexpected periods during which equipment or machinery is non-operational due to unforeseen events such as breakdowns, failures, malfunctions, or external factors like power outages or natural disasters'
                        )
                    },
                    {
                        name: 'planned_downtime',
                        field: row => row.availability.plannedDowntime,
                        label: this.$t('Planned downtime (min)'),
                        align: 'right',
                        tipText: this.$t(
                            'Scheduled periods during which equipment or machinery is intentionally taken offline for maintenance, repairs, upgrades, or planned production changes'
                        )
                    },
                    {
                        name: 'availability',
                        field: row => row.availability.availability,
                        label: this.$t('Availability'),
                        align: 'right',
                        class: 'availability-primary',
                        tipText: this.$t(
                            'Measures the actual production time as a percentage of the planned production time, accounting for downtime and equipment failures'
                        )
                    },
                    // OEE
                    {
                        name: 'oee',
                        field: 'oee',
                        label: this.$t('OEE'),
                        align: 'right',
                        tipText: this.$t(
                            'Combines availability, performance, and quality to provide a single metric that quantifies the overall effectiveness and productivity of the equipment'
                        )
                    }
                ],
                pagination: {
                    sortBy: row => row.availability.totalDowntime,
                    descending: true,
                    rowsPerPage: 0 // infinite
                }
            },
            // quality tooltip negative value message
            negativeValueMessage: this.$t(
                'Negative numbers are often a symptom of misconfiguration. Please verify the sensor configurations or contact support.'
            )
        };
    },
    computed: {
        ...mapGetters({
            getAsset: 'facilities/getAsset',
            getFacility: 'facilities/getFacility'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Get the units used by the facility's unit system
         * @return {Array} - List of units used by the facility's unit system
         */
        facilityUnits() {
            const options = getUnitSystemOptions(this.unitSystem);
            // the function returns { label, value } objects, so we need to iterate to get the unit values
            return options.map(option => option.value);
        },
        /**
         * Get the unit system used by the facility
         * @return {String} unit system
         */
        unitSystem() {
            return this.getFacility(this.facilityId)?.unit_system;
        }
    },
    methods: {
        formatProductionValue,
        formatPercentage,
        /**
         * Format a date to local date format
         * @param {String} datetime
         * @return {String} datetime in given locale.
         */
        formatDate(datetime) {
            return getLocaleFormat(datetime, this.userLocale);
        },
        /**
         * Formats the quantity columns for the totals rows
         * @param {Object} quantities
         * @return {String}
         */
        formatQuantities(quantities) {
            let quantityList = [];
            for (const [quantityUnit, quantityValue] of Object.entries(
                quantities
            )) {
                if (quantityValue !== 0) {
                    // convert to facility unit system
                    const { value: convertedValue, unit: convertedUnit } =
                        convertToUnitSystem(
                            quantityValue,
                            quantityUnit,
                            this.unitSystem
                        );

                    quantityList.push({
                        value: convertedValue,
                        unit: convertedUnit
                    });
                }
            }

            if (quantityList.length === 0) {
                return 0;
            }

            let output = '';
            for (let [index, quantity] of quantityList.entries()) {
                // Translators: {value} is the quantity value, {unit} is the quantity unit (e.g. kg).
                output += this.$t('{value} {unit}', {
                    value: this.formatProductionValue(
                        quantity.value,
                        this.userLocale
                    ),
                    unit: quantity.unit
                });
                if (index < quantityList.length - 1) {
                    output += ' | ';
                }
            }
            return output;
        },
        /*
         * Check if an asset is deleted
         * @param {String} assetId
         * @return {Boolean} - true if the asset is deleted
         */
        isAssetDeleted(assetId) {
            const asset = this.getAsset(assetId);
            if (asset) {
                return asset.is_deleted;
            }
            return false;
        },
        /**
         * Check if any of the quantity values are negative
         * @param {Object} quantities
         * @return {Boolean} - true if the value is negative
         */
        hasNegativeValue(quantities) {
            for (const quantityValue of Object.values(quantities)) {
                if (quantityValue < 0) {
                    return true;
                }
            }

            return false;
        }
    }
};
</script>

<style lang="scss" scoped>
$row-color: $secondary-white;

// table header
:deep(.sticky-table thead tr:nth-child(1) th) {
    background-color: $row-color;
}

// reports panel
:deep(.report-container) {
    overflow-y: scroll;
}

.availability-primary {
    background: $decorative-blue;
}
.performance-primary {
    background-color: $decorative-blue;
}
.quality-primary {
    background-color: $decorative-blue;
}

// sticky columns
:deep(.sticky-column) {
    background-color: white;
}

:deep(.sticky-column tr th) {
    background: white;
    position: sticky;
    z-index: 2;
}

:deep(.sticky-column thead tr:last-child th) {
    top: 48px;
    z-index: 3;
}

:deep(.sticky-column td:nth-child(1)) {
    background-color: white;
}

:deep(.sticky-column thead tr:nth-child(1) th) {
    top: 0;
    z-index: 1;
}

:deep(.sticky-column tr:nth-child(1) th:nth-child(1)) {
    z-index: 3;
}

:deep(.sticky-column td:nth-child(1)) {
    z-index: 1;
}

:deep(.sticky-column td:nth-child(1)),
:deep(.sticky-column th:nth-child(1)) {
    left: 0;
    position: sticky;
}

:deep(.expanded-child) {
    padding-left: 76px;
    text-align: left;
}
</style>
